import { html, nothing, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { LibElement, createUniqueId } from '../lib-element';
import { LibTagBoxOption } from './lib-tagbox-option';
import css from './lib-tagbox.css';

@customElement('lib-tagbox')
export class LibTagBox extends LibElement {
  static override readonly styles = [LibElement.hostStyles, unsafeCSS(css)];
  protected override get hostFocusSelector(): string { return 'input'; }
  @property() value = '';
  private revision = 0;

  static override get observedAttributes(): string[] { return [...super.observedAttributes, 'server-state']; }

  override attributeChangedCallback(name: string, previous: string | null, value: string | null): void {
    if (name === 'server-state') {
      if (value !== null) {
        const state = JSON.parse(value) as { value: string; revision: number };
        if (state.revision >= this.revision) this.value = state.value;
      }
      return;
    }
    if (name === 'value' && this.hasAttribute('server-state')) return;
    super.attributeChangedCallback(name, previous, value);
  }

  @property() headline = '';
  @property() placeholder = '';
  @property() surface = 'white';
  @property() message = '';
  @property({ attribute: 'no-matches-text' }) noMatchesText = 'No matches';
  @property({ attribute: 'remove-text' }) removeText = 'Remove';
  @property({ attribute: 'filter-mode' }) filterMode = 'contains';
  @property({ type: Boolean }) disabled = false;
  @property({ type: Boolean }) error = false;
  @property({ type: Number, attribute: 'max-tag-count' }) maxTagCount: number | null = null;
  @property({ attribute: 'aria-label' }) override ariaLabel: string | null = null;
  @state() private items: { value: string; text: string }[] = [];
  @state() private open = false;
  @state() private query = '';
  @state() private active = -1;
  private readonly idPrefix = createUniqueId('tagbox');
  private readonly observer = new MutationObserver(() => this.sync());
  private get atCapacity() { return this.maxTagCount !== null && this.selection.length >= this.maxTagCount; }
  private get selection() {
    return [...new Set(this.value.split(',').filter(value => value !== '' && this.items.some(item => item.value === value)))];
  }
  private get filtered() {
    const normalize = (text: string) => text.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
    const query = normalize(this.query);
    return this.items.filter(item => !this.selection.includes(item.value) &&
      (this.filterMode === 'startswith' ? normalize(item.text).startsWith(query) : normalize(item.text).includes(query)));
  }
  override connectedCallback() {
    super.connectedCallback();
    this.observer.observe(this, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['value'] });
    this.sync();
    document.addEventListener('pointerdown', this.outside, true);
  }
  override disconnectedCallback() {
    this.observer.disconnect();
    document.removeEventListener('pointerdown', this.outside, true);
    super.disconnectedCallback();
  }
  private sync() {
    this.items = Array.from(this.children).filter((child): child is LibTagBoxOption => child instanceof LibTagBoxOption)
      .map(option => ({ value: option.value, text: option.textContent ?? '' }))
      .filter((item, index, items) => items.findIndex(other => other.value === item.value) === index);
  }
  override updated() {
    if (this.disabled) this.close();
    if (this.atCapacity) this.active = -1;
    else if (this.active >= this.filtered.length) this.active = this.filtered.length - 1;
  }
  private readonly outside = (event: PointerEvent) => { if (!event.composedPath().includes(this)) this.close(); };
  private close() { this.open = false; this.query = ''; this.active = -1; }
  private show() { if (!this.disabled) { this.open = true; this.active = !this.atCapacity && this.filtered.length ? 0 : -1; } }
  private change(value: string, remove = false) {
    if (this.disabled || (!remove && this.atCapacity)) return;
    this.value = (remove ? this.selection.filter(item => item !== value) : [...new Set([...this.selection, value])]).join(',');
    this.revision++;
    this.close();
    this.dispatchEvent(new CustomEvent('lib-tagbox-change', {
      detail: { value: this.value, revision: this.revision }, bubbles: true, composed: true,
    }));
    if (!this.hasAttribute('server-state'))
      this.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
    this.renderRoot.querySelector<HTMLInputElement>('input')?.focus();
  }
  private async key(event: KeyboardEvent) {
    if (this.disabled) return;
    if (event.key === 'Tab' || event.key === 'Escape') { this.close(); return; }
    if (event.key === 'Backspace' && !this.query && this.selection.length) {
      event.preventDefault(); this.change(this.selection[this.selection.length - 1], true); return;
    }
    if (!['ArrowDown', 'ArrowUp', 'Enter', 'Home', 'End'].includes(event.key)) return;
    if (!this.open && ['Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    if (!this.open) {
      this.show();
      if (event.key === 'ArrowUp' && !this.atCapacity) this.active = this.filtered.length - 1;
      return;
    }
    if (this.atCapacity) return;
    if (event.key === 'Enter') {
      this.selectActive();
      return;
    }
    switch (event.key) {
      case 'ArrowDown':
        this.active = Math.min(this.active + 1, this.filtered.length - 1);
        break;
      case 'ArrowUp':
        this.active = Math.max(0, this.active - 1);
        break;
      case 'Home':
        this.active = 0;
        break;
      case 'End':
        this.active = this.filtered.length - 1;
        break;
    }
    await this.updateComplete;
    this.renderRoot.querySelector(`#${this.idPrefix}-option-${this.active}`)?.scrollIntoView({ block: 'nearest' });
  }
  private selectActive() {
    const item = this.filtered[this.active];
    if (item) this.change(item.value);
  }
  private renderListbox() {
    if (!this.open) return nothing;
    return html`<ul id="${this.idPrefix}-list" role="listbox" aria-multiselectable="true" aria-label=${this.ariaLabel ?? this.headline}>
      ${this.filtered.map((item, index) => html`<li id="${this.idPrefix}-option-${index}" role="option" aria-selected="false"
        aria-disabled=${this.atCapacity ? 'true' : nothing}
        class=${!this.atCapacity && index === this.active ? 'active' : ''} @pointerdown=${(event: PointerEvent) => event.preventDefault()}
        @click=${() => this.change(item.value)}>${item.text}</li>`)}
      ${!this.filtered.length ? html`<li role="status">${this.noMatchesText}</li>` : nothing}
    </ul>`;
  }
  override render() {
    const id = this.idPrefix;
    const invalid = this.error || !!this.message;
    return html`<div class="control ${invalid ? 'error' : ''} ${this.surface === 'grey' ? 'grey' : ''}">
      ${this.headline ? html`<label for="${id}">${this.headline}<slot name="help"></slot></label>` : nothing}
      <div class="anchor">
        <div class="field">
          ${this.selection.map(value => {
            const item = this.items.find(item => item.value === value);
            return item ? html`<span class="tag">${item.text}<button type="button" ?disabled=${this.disabled}
              aria-label="${this.removeText} ${item.text}" @click=${() => this.change(value, true)}>×</button></span>` : nothing;
          })}
          <input id="${id}" role="combobox" autocomplete="off" aria-autocomplete="list"
            aria-label=${this.ariaLabel ?? this.headline} aria-expanded="${this.open}"
            aria-controls="${id}-list" aria-invalid="${invalid}"
            aria-describedby=${this.message ? `${id}-message` : nothing}
            aria-activedescendant=${this.open && !this.atCapacity && this.filtered[this.active] ? `${id}-option-${this.active}` : nothing}
            ?disabled=${this.disabled} placeholder=${this.placeholder} .value=${this.query}
            @click=${() => this.show()} @keydown=${this.key}
            @blur=${(event: FocusEvent) => { if (!this.renderRoot.contains(event.relatedTarget as Node)) this.close(); }}
            @input=${(event: InputEvent) => { this.query = (event.target as HTMLInputElement).value; this.show(); }}>
        </div>
        ${this.renderListbox()}
      </div>
      ${this.message ? html`<div class="message" id="${id}-message">${this.message}</div>` : nothing}
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'lib-tagbox': LibTagBox; }
}
